import { Box, Popover } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import SolidGradientColorPicker from "../../../../../components/common/SolidGradientColorPicker/SolidGradientColorPicker";
import treeStyles from "../../../../../pages/Fibo/StructuredPrompt/FiboPromptTree/FiboPromptTree.module.scss";
import canvasStyles from "../StructuredPromptImageCanvas.module.scss";
import { hexToRgb, rgbToHex } from "../utils/colorUtils";

export interface RGBColor {
	r: number;
	g: number;
	b: number;
}

interface FiboColorPickerRgbProps {
	mode: "rgb";
	color: RGBColor;
	index: number;
	disabled?: boolean;
	onChange?: (index: number, color: RGBColor) => void;
}

interface FiboColorPickerHexProps {
	mode: "hex";
	color: string;
	disabled?: boolean;
	autoOpen?: boolean;
	onChange: (color: string) => void;
}

type FiboColorPickerProps = FiboColorPickerRgbProps | FiboColorPickerHexProps;

const rgbaToHex = (rgba: string): string => {
	const rgb = rgba.match(/\d+/g);
	return rgb ? rgbToHex(parseInt(rgb[0]), parseInt(rgb[1]), parseInt(rgb[2])) : "";
};

const FiboColorPicker = (props: FiboColorPickerProps) => {
	const { mode, disabled } = props;
	const isRgbMode = mode === "rgb";

	const rgbColor = isRgbMode ? props.color : null;
	const rgbIndex = isRgbMode ? props.index : 0;
	const rgbOnChange = isRgbMode ? props.onChange : undefined;
	const hexColor = !isRgbMode ? props.color : "";
	const hexOnChange = !isRgbMode ? props.onChange : undefined;
	const autoOpen = !isRgbMode ? props.autoOpen : false;

	const [localRgbColor, setLocalRgbColor] = useState<RGBColor>(rgbColor || { r: 0, g: 0, b: 0 });
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const colorBoxRef = useRef<HTMLElement>(null);

	useEffect(() => {
		if (autoOpen && colorBoxRef.current && !anchorEl) {
			setAnchorEl(colorBoxRef.current);
		}
	}, [autoOpen]);

	useEffect(() => {
		if (rgbColor) {
			setLocalRgbColor(rgbColor);
		}
	}, [rgbColor?.r, rgbColor?.g, rgbColor?.b]);

	const displayHex = useMemo(
		() => (isRgbMode ? rgbToHex(localRgbColor.r, localRgbColor.g, localRgbColor.b) : hexColor),
		[isRgbMode, hexColor, localRgbColor],
	);

	const handleColorPickerChange = (newColor: string) => {
		const hex = rgbaToHex(newColor);
		if (isRgbMode) {
			const rgb = hexToRgb(hex);
			if (rgb) {
				setLocalRgbColor(rgb);
				rgbOnChange?.(rgbIndex, rgb);
			}
		} else {
			hexOnChange?.(hex);
		}
	};

	const handleHexInputChange = (value: string) => {
		if (isRgbMode) {
			const rgb = hexToRgb(value);
			if (rgb) {
				setLocalRgbColor(rgb);
				rgbOnChange?.(rgbIndex, rgb);
			}
		} else {
			hexOnChange?.(value);
		}
	};

	const popoverStyles = isRgbMode ? treeStyles : canvasStyles;

	const colorPickerPopover = (
		<Popover
			open={Boolean(anchorEl)}
			anchorEl={anchorEl}
			onClose={() => setAnchorEl(null)}
			anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			transformOrigin={{ vertical: "top", horizontal: "left" }}
			slotProps={{ paper: { className: popoverStyles.colorPickerPopover } }}
		>
			<SolidGradientColorPicker
				hideColorTypeBtns
				hidePresets
				hideColorGuide
				hideAdvancedSliders
				hideOpacity
				color={displayHex}
				onChange={handleColorPickerChange}
				height={150}
				width={200}
				style={{
					rbgcpControlBtnWrapper: { marginTop: -4, marginBottom: -12 },
					rbgcpInputsWrap: { display: "flex", gap: 8 },
					rbgcpInput: { height: 20, fontSize: 12 },
					rbgcpInputLabel: { marginTop: 4, fontSize: 10 },
				}}
			/>
		</Popover>
	);

	if (!isRgbMode) {
		return (
			<Box className={canvasStyles.compactColorPickerWrapper}>
				<Box
					ref={colorBoxRef}
					className={canvasStyles.colorBox}
					sx={{ backgroundColor: hexColor }}
					onClick={(e) => !disabled && setAnchorEl(anchorEl ? null : e.currentTarget)}
				/>
				<input
					type="text"
					className={canvasStyles.colorInput}
					value={hexColor}
					onChange={(e) => handleHexInputChange(e.target.value)}
					disabled={disabled}
				/>
				{colorPickerPopover}
			</Box>
		);
	}

	return (
		<Box className={treeStyles.colorValueContainer}>
			<span className={treeStyles.colorIndex}>[{rgbIndex}]:</span>
			<Box
				className={treeStyles.colorSquare}
				style={{ backgroundColor: displayHex }}
				onClick={(e) => !disabled && setAnchorEl(anchorEl ? null : e.currentTarget)}
			/>
			{colorPickerPopover}
			<input
				type="text"
				className={treeStyles.hexInput}
				value={displayHex}
				onChange={(e) => handleHexInputChange(e.target.value)}
				disabled={disabled}
			/>
		</Box>
	);
};

export default FiboColorPicker;
